import {
  FRACTIONAL_INDEX_END,
  FRACTIONAL_INDEX_START,
  asciiCompare,
  getIndicesBetween,
} from "../fractionalIndexing.js";
import { CellId } from "../idTypeBrands.js";
import { typedObjectEntries } from "../utils/typedObjects.js";

export interface SortableCell {
  id: CellId;
  order: string;
  parentCellId?: CellId | null;
}

function sortableCellCompareFn<T extends SortableCell>(a: T, b: T): number {
  return asciiCompare(a.order, b.order);
}

export interface SortedCellNode<T extends SortableCell> {
  cell: T;
  children?: SortedCellNode<T>[];
}

/**
 * Put a list of cells into sorted order.
 *
 * This accounts for parent cells that can affect ordering so the result is tree like.
 * To get a flattened list, use {@link flattenSortedCells}
 */
export function sortCells<T extends SortableCell>(
  cells: readonly T[],
): SortedCellNode<T>[] {
  const cellIdToCell = cells.reduce<Record<CellId, T>>((acc, cell) => {
    acc[cell.id] = cell;
    return acc;
  }, {});

  const cellIdToChildren = cells.reduce<Record<CellId, T[] | undefined>>(
    (acc, cell) => {
      if (cell.parentCellId != null) {
        const prevChildren = (acc[cell.parentCellId] ??= []);
        prevChildren.push(cell);
      }
      return acc;
    },
    {},
  );

  const rootCells = cells.filter(
    (cell) =>
      cell.parentCellId == null || cellIdToCell[cell.parentCellId] == null,
  );

  // now we do a recursive graph search starting with cells with a depth of zero
  return recursiveSortCells(rootCells, cellIdToChildren);
}

/**
 * Helper function to actually assemble the 'tree' of sorted cells
 */
function recursiveSortCells<T extends SortableCell>(
  cells: T[],
  cellIdToChildren: Record<CellId, T[] | undefined>,
): SortedCellNode<T>[] {
  return cells.toSorted(sortableCellCompareFn).map((cell) => {
    const rawChildren = cellIdToChildren[cell.id];

    const children =
      rawChildren != null
        ? recursiveSortCells(rawChildren, cellIdToChildren)
        : undefined;

    return { cell, children };
  });
}

/**
 * Flatten out a collection of sorted cells from its tree like structure
 * into a simple sorted array.
 *
 * If you want a list of these cells with a 'corrected' order use {@link interpolateSortedCellOrders}
 */
export function flattenSortedCells<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
): T[] {
  return sortedCells.reduce<T[]>((acc, node) => {
    acc.push(node.cell);
    if (node.children != null) {
      acc.push(...flattenSortedCells(node.children));
    }
    return acc;
  }, []);
}

/**
 * Given a collection of sorted cells, provide interpolated orders
 * such that children can be sorted globally regardless of depth
 */
export function interpolateSortedCellOrders<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
): Record<CellId, string> {
  const flattenedCells = flattenSortedCells(sortedCells);

  const orders = getIndicesBetween({
    start: FRACTIONAL_INDEX_START,
    end: FRACTIONAL_INDEX_END,
    count: flattenedCells.length,
  });

  const interpolatedOrders: Record<CellId, string> = {};

  for (const [idx, cell] of flattenedCells.entries()) {
    interpolatedOrders[cell.id] = orders[idx]!;
  }

  return interpolatedOrders;
}

export type SortedCellFunctionalCallback<T extends SortableCell, R> = (
  /**
   * The current cell
   */
  value: T,
  info: {
    /**
     * How deep in the tree the current cell is
     */
    depth: number;
    /**
     * The index of the current cell among its siblings
     */
    index: number;
    /**
     * The path of cell ids that lead to the current cell
     */
    path: CellId[];
  },
) => R;

/**
 * Transform each cell value using a callback function
 */
export function mapSortedCells<
  T extends SortableCell,
  R extends SortableCell = T,
>(
  sortedCells: readonly SortedCellNode<T>[],
  callbackfn: SortedCellFunctionalCallback<T, R>,
): SortedCellNode<R>[] {
  return recursiveMapSortedCells(sortedCells, callbackfn, {
    depth: 0,
    path: [],
  });
}

function recursiveMapSortedCells<
  T extends SortableCell,
  R extends SortableCell = T,
>(
  sortedCells: readonly SortedCellNode<T>[],
  callbackfn: SortedCellFunctionalCallback<T, R>,
  {
    depth,
    path,
  }: {
    depth: number;
    path: CellId[];
  },
): SortedCellNode<R>[] {
  return sortedCells.map((node, index) => ({
    cell: callbackfn(node.cell, { depth, index, path }),
    children:
      node.children != null
        ? recursiveMapSortedCells(node.children, callbackfn, {
            depth: depth + 1,
            path: [...path, node.cell.id],
          })
        : undefined,
  }));
}

/**
 * If a cell does not match `predicate` remove it and all
 * its children from the tree
 */
export function filterSortedCells<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
  predicate: SortedCellFunctionalCallback<T, unknown>,
): SortedCellNode<T>[] {
  return recursivefilterSortedCells(sortedCells, predicate, {
    depth: 0,
    path: [],
  });
}

function recursivefilterSortedCells<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
  predicate: SortedCellFunctionalCallback<T, unknown>,
  {
    depth,
    path,
  }: {
    depth: number;
    path: CellId[];
  },
): SortedCellNode<T>[] {
  return sortedCells
    .filter((node, index) => predicate(node.cell, { depth, index, path }))
    .map((node) => ({
      cell: node.cell,
      children:
        node.children != null
          ? recursivefilterSortedCells(node.children, predicate, {
              depth: depth + 1,
              path: [...path, node.cell.id],
            })
          : undefined,
    }));
}

/**
 * If a cell does not match `predicate`, prune its children
 * but keep it in the list.
 */
export function filterSortedCellsChildren<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
  predicate: SortedCellFunctionalCallback<T, unknown>,
): SortedCellNode<T>[] {
  return recursiveFilterSortedCellsChildren(sortedCells, predicate, {
    depth: 0,
    path: [],
  });
}

function recursiveFilterSortedCellsChildren<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
  predicate: SortedCellFunctionalCallback<T, unknown>,
  {
    depth,
    path,
  }: {
    depth: number;
    path: CellId[];
  },
): SortedCellNode<T>[] {
  return sortedCells.map((node, index) => ({
    cell: node.cell,
    children:
      node.children != null && predicate(node.cell, { depth, index, path })
        ? recursiveFilterSortedCellsChildren(node.children, predicate, {
            depth: depth + 1,
            path: [...path, node.cell.id],
          })
        : undefined,
  }));
}

/**
 * Create a record of cells to the ids of all ancestor cells each cell has
 */
export function cellIdToAncestorIds<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
): Record<CellId, Set<CellId>> {
  const acc: Record<CellId, Set<CellId>> = {};
  recursiveCellIdToAncestorIds({ acc, path: [], sortedCells });
  return acc;
}

// do a recursive DFS to build up a mapping of all ancestors for a specific cell
function recursiveCellIdToAncestorIds<T extends SortableCell>({
  acc,
  path,
  sortedCells,
}: {
  acc: Record<CellId, ReadonlySet<CellId>>;
  path: readonly CellId[];
  sortedCells: readonly SortedCellNode<T>[];
}): void {
  const currentAncestors = new Set(path);
  for (const node of sortedCells) {
    acc[node.cell.id] = currentAncestors;
    const nextPath = [node.cell.id, ...path];
    if (node.children != null) {
      recursiveCellIdToAncestorIds({
        acc,
        path: nextPath,
        sortedCells: node.children,
      });
    }
  }
}

/**
 * Create a record of cells to the ids of all descendent cells each cell has
 */
export function cellIdToDescendentIds<T extends SortableCell>(
  sortedCells: readonly SortedCellNode<T>[],
): Record<CellId, Set<CellId> | undefined> {
  return typedObjectEntries(cellIdToAncestorIds(sortedCells)).reduce<
    Record<CellId, Set<CellId>>
  >((acc, [cellId, ancestorIds]) => {
    for (const ancestorId of ancestorIds) {
      const prev = (acc[ancestorId] ??= new Set());
      prev.add(cellId);
    }
    return acc;
  }, {});
}
